import React, {forwardRef, MutableRefObject, useEffect, useRef} from "react";
import {css} from "@emotion/react";

import {flex, flexDirection, onDesktop, p} from "@pg-design/helpers-css";
import {MapMarkerOutlineIcon} from "@pg-design/icons-module";
import {Tabs} from "@pg-design/tabs";
import {FormActions} from "@pg-mono/form";
import {useCombinedRefs} from "@pg-mono/hooks";
import {useUserDevice} from "@pg-mono/user-device";

import {useOfferListingAbroadView} from "../../../offer/list/hooks/use_offer_listing_abroad_view_check";
import {IFetchAllSearchListsParams} from "../../actions/fetch_search_all_action";
import {searchFormFields} from "../../constants/search_form_constants";
import useSearchAndFilterUpdate from "../../hooks/use_search_and_filter_update";
import {RegionSearchTabsType} from "../../types/RegionSearchTabsType";
import {SearchFormValuesType} from "../../types/SearchFormValuesType";
import {SearchTab} from "../../types/SearchTab";
import {SearchDistanceFilter} from "../SearchDistanceFilter";
import {SearchDistanceFilterMobile} from "../SearchDistanceFilterMobile";
import {ISearchAutocompleteFormValues, SearchAutocomplete} from "./SearchAutocomplete";
import {
    ISearchAutocompleteDropdownWithTimeTravelProps,
    SearchAutocompleteDropdownWithRegionAdjustments
} from "./SearchAutocompleteDropdownWithRegionAdjustments";
import {SlotOrderType} from "./SearchAutocompleteInputWithSlot";

/* eslint-disable @typescript-eslint/no-explicit-any */
interface IProps extends Omit<ISearchAutocompleteDropdownWithTimeTravelProps, "updateSelectedRegions" | "isSearchButtonActive" | "getFieldProps"> {
    defaultDistanceValue: number;
    fetchAllSearchLists: (searchInput: string, params: IFetchAllSearchListsParams) => Promise<SearchTab>;
    formActions: FormActions<Record<keyof typeof searchFormFields, any>>;
    formValues: SearchFormValuesType;
    isDropdownOpen?: boolean;
    onDropdownStatusChange: (isOpen: boolean) => void;
    onAfterChange: ReturnType<typeof useSearchAndFilterUpdate>["onAfterChange"];
    onAfterTabSwitch: () => void;
    onChange: (values: Partial<ISearchAutocompleteFormValues>) => void;
    onInputMount: (ref: MutableRefObject<HTMLInputElement | null>) => void;
    setDesktopTypeBlendActive: React.Dispatch<React.SetStateAction<boolean>>;
    slotOrder?: SlotOrderType;
    blockBodyScroll?: boolean;
}

export const SearchAutocompleteWithRegionAdjustments = forwardRef<HTMLDivElement, IProps>((props, ref) => {
    const {
        activeRegionSearchTab,
        defaultDistanceValue,
        isInModal,
        onAfterChange,
        onAfterTabSwitch,
        onChange,
        onDropdownStatusChange,
        onInputMount,
        onSearchSubmit,
        setDesktopTypeBlendActive,
        regionSearchTabChangeHandler,
        regionSearchTabs
    } = props;
    const isTravelTabActive = activeRegionSearchTab === RegionSearchTabsType.TRAVEL_TIME;
    const isAbroadOffersView = useOfferListingAbroadView();
    const localWrapperRef = useRef<HTMLDivElement | null>(null);
    const wrapperRef = useCombinedRefs<HTMLDivElement>(ref, localWrapperRef);
    const {isMobile} = useUserDevice();

    useEffect(() => {
        if (props.formValues.search.label && wrapperRef.current && wrapperRef.current.scrollTop === 0) {
            wrapperRef.current.scrollTop = 20;
        }
    }, [props.formValues.search.tabType === SearchTab.Regions && props.formValues.search.label]);

    const onInputClick = (defaultAction: () => void) => {
        defaultAction();

        if (!isMobile || !isInModal) {
            return;
        }

        requestAnimationFrame(() => {
            if (wrapperRef.current) {
                // for mobile only - we need to display as many results in the viewport as possible, due to the virtual keyboard attached to bottom
                const tabsHeight = wrapperRef.current.children[0]?.scrollHeight || 0;
                wrapperRef.current.scrollTo({
                    top: tabsHeight,
                    behavior: "smooth"
                });
            }
        });
    };

    return (
        <div css={searchAutocompleteWrapper} ref={wrapperRef}>
            {isInModal && (
                <Tabs
                    activeColor="full-dark"
                    onChange={regionSearchTabChangeHandler}
                    color="gray"
                    tabs={regionSearchTabs}
                    value={activeRegionSearchTab ?? undefined}
                    variant="line"
                    fullWidth
                />
            )}
            <SearchAutocomplete
                values={props.formValues}
                onChange={onChange}
                onAfterChange={onAfterChange}
                handleDistanceSubmit={onSearchSubmit}
                onDropdownStatusChange={onDropdownStatusChange}
                onAfterTabSwitch={onAfterTabSwitch}
                inputIcon={<MapMarkerOutlineIcon size="2.4" />}
                placeholder={isTravelTabActive ? "Wpisz dokładny adres" : "Gdzie chcesz mieszkać?"}
                fetchAllSearchLists={props.fetchAllSearchLists}
                isInModal={isInModal}
                onInputMount={!isInModal ? onInputMount : undefined}
                onInputClick={onInputClick}
                disableMultiRegion={isAbroadOffersView}
                isDropdownOpen={props.isDropdownOpen}
                blockBodyScroll={props.blockBodyScroll}
                slot={
                    <>
                        {isInModal && !isTravelTabActive ? (
                            <SearchDistanceFilterMobile
                                defaultValue={defaultDistanceValue}
                                values={props.formValues}
                                onAfterChange={onAfterChange}
                                onChange={props.formActions.update}
                                handleDistanceSubmit={onSearchSubmit}
                                setDesktopTypeBlendActive={setDesktopTypeBlendActive}
                            />
                        ) : null}
                        {!isInModal && !isTravelTabActive ? (
                            <SearchDistanceFilter
                                defaultValue={defaultDistanceValue}
                                values={props.formValues}
                                handleDistanceAfterChange={onAfterChange}
                                onChange={props.formActions.update}
                                handleDistanceSubmit={onSearchSubmit}
                                setDesktopTypeBlendActive={setDesktopTypeBlendActive}
                            />
                        ) : null}
                    </>
                }
                slotOrder={isInModal ? SlotOrderType.ROW : props.slotOrder}
                renderDropdownList={(dropdownProps) => {
                    return <SearchAutocompleteDropdownWithRegionAdjustments {...props} {...dropdownProps} />;
                }}
                isTravelTimeTabActive={isTravelTabActive}
                searchBarContainerStyle={isInModal && activeRegionSearchTab === RegionSearchTabsType.DISTRICTS && searchBarContainerWithActiveDistrictTabStyle}
            />
        </div>
    );
});

const searchAutocompleteWrapper = css`
    ${flex()};
    ${flexDirection("column")};
    position: relative;
    flex-grow: 1;
    flex-shrink: 1;
    overflow: auto;

    ${onDesktop(css`
        overflow: initial;
        ${p(0)};
        max-height: 100%;
        width: 100%;
        min-width: 360px;
    `)};
`;

const searchBarContainerWithActiveDistrictTabStyle = css`
    display: none;
`;
import React, {forwardRef, MutableRefObject, useEffect, useRef} from "react";
import {css} from "@emotion/react";

import {flex, flexDirection, onDesktop, p} from "@pg-design/helpers-css";
import {MapMarkerOutlineIcon} from "@pg-design/icons-module";
import {Tabs} from "@pg-design/tabs";
import {FormActions} from "@pg-mono/form";
import {useCombinedRefs} from "@pg-mono/hooks";
import {useUserDevice} from "@pg-mono/user-device";

import {useOfferListingAbroadView} from "../../../offer/list/hooks/use_offer_listing_abroad_view_check";
import {IFetchAllSearchListsParams} from "../../actions/fetch_search_all_action";
import {searchFormFields} from "../../constants/search_form_constants";
import useSearchAndFilterUpdate from "../../hooks/use_search_and_filter_update";
import {RegionSearchTabsType} from "../../types/RegionSearchTabsType";
import {SearchFormValuesType} from "../../types/SearchFormValuesType";
import {SearchTab} from "../../types/SearchTab";
import {SearchDistanceFilter} from "../SearchDistanceFilter";
import {SearchDistanceFilterMobile} from "../SearchDistanceFilterMobile";
import {ISearchAutocompleteFormValues, SearchAutocomplete} from "./SearchAutocomplete";
import {
    ISearchAutocompleteDropdownWithTimeTravelProps,
    SearchAutocompleteDropdownWithRegionAdjustments
} from "./SearchAutocompleteDropdownWithRegionAdjustments";
import {SlotOrderType} from "./SearchAutocompleteInputWithSlot";

/* eslint-disable @typescript-eslint/no-explicit-any */
interface IProps extends Omit<ISearchAutocompleteDropdownWithTimeTravelProps, "updateSelectedRegions" | "isSearchButtonActive" | "getFieldProps"> {
    defaultDistanceValue: number;
    fetchAllSearchLists: (searchInput: string, params: IFetchAllSearchListsParams) => Promise<SearchTab>;
    formActions: FormActions<Record<keyof typeof searchFormFields, any>>;
    formValues: SearchFormValuesType;
    isDropdownOpen?: boolean;
    onDropdownStatusChange: (isOpen: boolean) => void;
    onAfterChange: ReturnType<typeof useSearchAndFilterUpdate>["onAfterChange"];
    onAfterTabSwitch: () => void;
    onChange: (values: Partial<ISearchAutocompleteFormValues>) => void;
    onInputMount: (ref: MutableRefObject<HTMLInputElement | null>) => void;
    setDesktopTypeBlendActive: React.Dispatch<React.SetStateAction<boolean>>;
    slotOrder?: SlotOrderType;
    blockBodyScroll?: boolean;
}

export const SearchAutocompleteWithRegionAdjustments = forwardRef<HTMLDivElement, IProps>((props, ref) => {
    const {
        activeRegionSearchTab,
        defaultDistanceValue,
        isInModal,
        onAfterChange,
        onAfterTabSwitch,
        onChange,
        onDropdownStatusChange,
        onInputMount,
        onSearchSubmit,
        setDesktopTypeBlendActive,
        regionSearchTabChangeHandler,
        regionSearchTabs
    } = props;
    const isTravelTabActive = activeRegionSearchTab === RegionSearchTabsType.TRAVEL_TIME;
    const isAbroadOffersView = useOfferListingAbroadView();
    const localWrapperRef = useRef<HTMLDivElement | null>(null);
    const wrapperRef = useCombinedRefs<HTMLDivElement>(ref, localWrapperRef);
    const {isMobile} = useUserDevice();

    useEffect(() => {
        if (props.formValues.search.label && wrapperRef.current && wrapperRef.current.scrollTop === 0) {
            wrapperRef.current.scrollTop = 20;
        }
    }, [props.formValues.search.tabType === SearchTab.Regions && props.formValues.search.label]);

    const onInputClick = (defaultAction: () => void) => {
        defaultAction();

        if (!isMobile || !isInModal) {
            return;
        }

        requestAnimationFrame(() => {
            if (wrapperRef.current) {
                // for mobile only - we need to display as many results in the viewport as possible, due to the virtual keyboard attached to bottom
                const tabsHeight = wrapperRef.current.children[0]?.scrollHeight || 0;
                wrapperRef.current.scrollTo({
                    top: tabsHeight,
                    behavior: "smooth"
                });
            }
        });
    };

    return (
        <div css={searchAutocompleteWrapper} ref={wrapperRef}>
            {isInModal && (
                <Tabs
                    activeColor="full-dark"
                    onChange={regionSearchTabChangeHandler}
                    color="gray"
                    tabs={regionSearchTabs}
                    value={activeRegionSearchTab ?? undefined}
                    variant="line"
                    fullWidth
                />
            )}
            <SearchAutocomplete
                values={props.formValues}
                onChange={onChange}
                onAfterChange={onAfterChange}
                handleDistanceSubmit={onSearchSubmit}
                onDropdownStatusChange={onDropdownStatusChange}
                onAfterTabSwitch={onAfterTabSwitch}
                inputIcon={<MapMarkerOutlineIcon size="2.4" />}
                placeholder={isTravelTabActive ? "Wpisz dokładny adres" : "Gdzie chcesz mieszkać?"}
                fetchAllSearchLists={props.fetchAllSearchLists}
                isInModal={isInModal}
                onInputMount={!isInModal ? onInputMount : undefined}
                onInputClick={onInputClick}
                disableMultiRegion={isAbroadOffersView}
                isDropdownOpen={props.isDropdownOpen}
                blockBodyScroll={props.blockBodyScroll}
                slot={
                    <>
                        {isInModal && !isTravelTabActive ? (
                            <SearchDistanceFilterMobile
                                defaultValue={defaultDistanceValue}
                                values={props.formValues}
                                onAfterChange={onAfterChange}
                                onChange={props.formActions.update}
                                handleDistanceSubmit={onSearchSubmit}
                                setDesktopTypeBlendActive={setDesktopTypeBlendActive}
                            />
                        ) : null}
                        {!isInModal && !isTravelTabActive ? (
                            <SearchDistanceFilter
                                defaultValue={defaultDistanceValue}
                                values={props.formValues}
                                handleDistanceAfterChange={onAfterChange}
                                onChange={props.formActions.update}
                                handleDistanceSubmit={onSearchSubmit}
                                setDesktopTypeBlendActive={setDesktopTypeBlendActive}
                            />
                        ) : null}
                    </>
                }
                slotOrder={isInModal ? SlotOrderType.ROW : props.slotOrder}
                renderDropdownList={(dropdownProps) => {
                    return <SearchAutocompleteDropdownWithRegionAdjustments {...props} {...dropdownProps} />;
                }}
                isTravelTimeTabActive={isTravelTabActive}
                searchBarContainerStyle={isInModal && activeRegionSearchTab === RegionSearchTabsType.DISTRICTS && searchBarContainerWithActiveDistrictTabStyle}
            />
        </div>
    );
});

const searchAutocompleteWrapper = css`
    ${flex()};
    ${flexDirection("column")};
    position: relative;
    flex-grow: 1;
    flex-shrink: 1;
    overflow: auto;

    ${onDesktop(css`
        overflow: initial;
        ${p(0)};
        max-height: 100%;
        width: 100%;
        min-width: 360px;
    `)};
`;

const searchBarContainerWithActiveDistrictTabStyle = css`
    display: none;
`;
